/**
 * DenominationCounter — počítadlo hotovosti pro uzávěrku pokladny rozvozce.
 *
 * Layout:
 *   ┌─ NAPOČÍTÁNO ──────────── X bankovek ─┐
 *   │            0 Kč                       │  ← velký total
 *   │   Očekáváno: 1219 Kč · zbývá 1219     │
 *   └───────────────────────────────────────┘
 *                                ↶ Zpět · 🗑 Vynulovat
 *
 *   BANKOVKY
 *   [2000] [1000] [500]
 *   [200]  [100]
 *
 *   MINCE
 *   [50] [20] [10] [5]
 *   [2]  [1]
 *
 * Tlačítka: click = +1, long-press (≥500 ms) = +5. Aktivní (count > 0)
 * má oranžový border + accent text + accent tint bg. Pulsing border ve
 * stavu když je očekávaná částka > 0 a total < očekávaná (vede uživatele
 * k pokračování v napočtu).
 */
import { useRef, useMemo } from 'react';
import { Undo2, RotateCcw, Pencil, Calculator } from 'lucide-react';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from '../../pages/MojeSmena/sheetStyle';

const BANKOVKY = [2000, 1000, 500, 200, 100];
const MINCE = [50, 20, 10, 5, 2, 1];

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

const LONG_PRESS_MS = 500;
const LONG_PRESS_BULK = 5;
const ACCENT = '#FF8A00';

/**
 * Props:
 *   denominace        — { 2000: 0, 1000: 1, ... } map nominal → count
 *   onChange(novyMap) — denom map se změnila (klik na tile / Undo / Vynulovat)
 *   manualAmount      — null = denom mode (tiles aktivní), number = manual mode
 *                       (vstup editovatelný klávesnicí, tiles ztlumené)
 *   onManualAmountChange(value) — null vypne manual mode, number nastaví částku
 *   ocekavanaCastka   — pro live diff display
 *   radekText         — text do řádku pod součtem místo „Očekáváno…"
 *   bezPrepinace      — bez pilulky Ručně/Klikat (přepínač má rodič)
 *   isDark            — barvy podle theme
 */
export default function DenominationCounter({
    denominace = {},
    onChange,
    manualAmount = null,
    onManualAmountChange,
    ocekavanaCastka = null,
    radekText = null,
    bezPrepinace = false,
    isDark = true,
}) {
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);
    const manualMode = manualAmount !== null && manualAmount !== undefined;

    const historieRef = useRef([]);
    const longPressTimerRef = useRef(null);
    const longPressFiredRef = useRef(false);

    // Live total z denom mapy (klikací mode) — v manual mode total = manualAmount
    const { totalZDenom, pocetKusu } = useMemo(() => {
        let s = 0, ks = 0;
        for (const [nominal, count] of Object.entries(denominace || {})) {
            s += Number(nominal) * Number(count || 0);
            ks += Number(count || 0);
        }
        return { totalZDenom: s, pocetKusu: ks };
    }, [denominace]);
    const total = manualMode ? Number(manualAmount || 0) : totalZDenom;

    const diff = ocekavanaCastka == null ? null : total - Number(ocekavanaCastka);
    const zbyva = diff == null ? null : Math.max(0, -diff);

    function pridej(nominal, delta) {
        const aktualni = Number(denominace[nominal] || 0);
        const novy = Math.max(0, aktualni + delta);
        const skutecnaDelta = novy - aktualni;
        if (skutecnaDelta === 0) return;
        historieRef.current.push({ nominal, delta: skutecnaDelta });
        onChange?.({ ...denominace, [nominal]: novy });
    }

    function handlePointerDown(nominal) {
        longPressFiredRef.current = false;
        longPressTimerRef.current = setTimeout(() => {
            longPressFiredRef.current = true;
            pridej(nominal, LONG_PRESS_BULK);
        }, LONG_PRESS_MS);
    }

    function handlePointerUp(nominal) {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        if (!longPressFiredRef.current) {
            pridej(nominal, 1);
        }
        longPressFiredRef.current = false;
    }

    function handlePointerCancel() {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
        longPressFiredRef.current = false;
    }

    function handleUndo() {
        const posledni = historieRef.current.pop();
        if (!posledni) return;
        const aktualni = Number(denominace[posledni.nominal] || 0);
        const novy = Math.max(0, aktualni - posledni.delta);
        onChange?.({ ...denominace, [posledni.nominal]: novy });
    }

    function handleClear() {
        if (total === 0) return;
        if (!window.confirm('Vynulovat všechny počty?')) return;
        historieRef.current = [];
        const cleared = {};
        [...BANKOVKY, ...MINCE].forEach(d => { cleared[d] = 0; });
        onChange?.(cleared);
    }

    // Stav pro pulsing — directs attention dokud ještě uživatel nedoplnil očekávané
    const podsvit = ocekavanaCastka != null && total < ocekavanaCastka;
    const matchOk = ocekavanaCastka != null && Math.abs(total - ocekavanaCastka) < 0.5;

    function toggleManualMode() {
        if (manualMode) {
            // Vrátit do denom režimu — manualAmount = null, denom zůstává (uživatel
            // se může vrátit k tomu, co měl naklikané).
            onManualAmountChange?.(null);
        } else {
            // Aktivovat manual mode — předvyplň aktuální součet (z denom). Denom
            // map vyčistíme: server pak skipuje sum-check (audit per denominace
            // nemá smysl, když uživatel zadal jen výsledek).
            const startovaciCastka = totalZDenom || 0;
            const cleared = {};
            [...BANKOVKY, ...MINCE].forEach(d => { cleared[d] = 0; });
            onChange?.(cleared);
            historieRef.current = [];
            onManualAmountChange?.(startovaciCastka);
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Header karta NAPOČÍTÁNO — v denom modu velký nečíselný total,
                v manual modu editovatelný input. Pill akce + Ručně/Klikat toggle. */}
            <div style={{
                position: 'relative',
                padding: '14px 16px',
                borderRadius: SHEET_TOKENS.radius.card,
                background: matchOk
                    ? 'rgba(16,185,129,0.08)'
                    : (podsvit ? `${ACCENT}10` : inner.background),
                border: `1px solid ${matchOk ? '#10B981' : (podsvit ? ACCENT : inner.border.split(' ').pop())}`,
                display: 'flex', flexDirection: 'column', gap: 6,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{
                        fontSize: SHEET_TOKENS.typography.label.size,
                        fontWeight: SHEET_TOKENS.typography.label.weight,
                        letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                        textTransform: SHEET_TOKENS.typography.label.textTransform,
                        color: colors.labelMuted,
                    }}>{manualMode
                        ? 'Zadáno ručně'
                        : `Napočítáno · ${pocetKusu} ${pocetKusu === 1 ? 'kus' : pocetKusu >= 2 && pocetKusu <= 4 ? 'kusy' : 'kusů'}`}</span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        {/* Toggle Ručně ↔ Klikat */}
                        {!bezPrepinace && (
                            <button type="button" onClick={toggleManualMode}
                                style={pillButton(colors, true, manualMode)}>
                                {manualMode ? <><Calculator size={12} /> Klikat</> : <><Pencil size={12} /> Ručně</>}
                            </button>
                        )}
                        {!manualMode && (
                            <>
                                <button type="button" onClick={handleUndo}
                                    disabled={historieRef.current.length === 0}
                                    style={pillButton(colors, historieRef.current.length > 0)}>
                                    <Undo2 size={12} /> Zpět
                                </button>
                                <button type="button" onClick={handleClear}
                                    disabled={total === 0}
                                    style={pillButton(colors, total > 0)}>
                                    <RotateCcw size={12} /> Vynulovat
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {manualMode ? (
                    <div style={{
                        display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4,
                        padding: '4px 0',
                    }}>
                        <input
                            type="text"
                            inputMode="decimal"
                            autoFocus
                            value={manualAmount === 0 ? '' : String(manualAmount ?? '')}
                            onChange={e => {
                                const v = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                                const num = v === '' ? 0 : parseFloat(v);
                                onManualAmountChange?.(Number.isFinite(num) ? num : 0);
                            }}
                            placeholder="0"
                            style={{
                                width: '60%', minWidth: 0,
                                background: 'transparent', border: 'none', outline: 'none',
                                color: total > 0 ? ACCENT : colors.textPrimary,
                                fontSize: 30, fontWeight: 800, lineHeight: 1, letterSpacing: -0.6,
                                fontVariantNumeric: 'tabular-nums', textAlign: 'right',
                                padding: 0,
                                borderBottom: `2px solid ${ACCENT}`,
                            }}
                        />
                        <span style={{
                            fontSize: 18, fontWeight: 700, color: colors.textMuted,
                        }}>Kč</span>
                    </div>
                ) : (
                    <div style={{
                        fontSize: 30, fontWeight: 800,
                        color: matchOk ? '#10B981' : (total > 0 ? ACCENT : colors.textPrimary),
                        fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: -0.6,
                        textAlign: 'center', padding: '4px 0',
                    }}>{fmtKc(total)}</div>
                )}
                {/* Pevná výška: text se při počítání mění a nominály pod ním
                    se nesmí pohnout pod prstem. */}
                {(ocekavanaCastka != null || radekText) && (
                    <div style={{
                        fontSize: 11.5, lineHeight: '18px', minHeight: 18, color: colors.textMuted,
                        textAlign: 'center', fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                        {radekText ?? (
                            <>
                                Očekáváno: {fmtKc(ocekavanaCastka)}
                                {zbyva > 0 && <> · zbývá <strong style={{ color: ACCENT, fontSize: 12.5 }}>{fmtKc(zbyva)}</strong></>}
                                {diff > 0.5 && <> · <strong style={{ color: ACCENT, fontSize: 12.5 }}>+{fmtKc(diff)}</strong> přebytek</>}
                                {matchOk && <> · <strong style={{ color: '#10B981', fontSize: 12.5 }}>sedí ✓</strong></>}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* BANKOVKY — 3 sloupce × 2 řádky (5 nominálů: 3+2). Stejná
                velikost dlaždice jako MINCE, aby vznikla pravidelná mřížka.
                V manual modu jsou tiles dimmed (opacity 0.4) a neklikatelné. */}
            <DenomSection
                label="Bankovky"
                denoms={BANKOVKY}
                cols={3}
                size="large"
                denominace={denominace}
                isDark={isDark}
                disabled={manualMode}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                colors={colors}
                inner={inner}
            />

            {/* MINCE — 3 sloupce × 2 řádky (3+3 = 6 nominálů). Label nese hint o
                long-pressu. Identický rozměr jako bankovky → uniformní rytmus mřížky. */}
            <DenomSection
                label="Mince · klik +1, podrž +5"
                denoms={MINCE}
                cols={3}
                size="large"
                denominace={denominace}
                isDark={isDark}
                disabled={manualMode}
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                colors={colors}
                inner={inner}
            />
        </div>
    );
}

// ─── Sekce (Bankovky / Mince) ─────────────────────────────────────────────────

function DenomSection({ label, denoms, cols, size, denominace, isDark, disabled = false, onPointerDown, onPointerUp, onPointerCancel, colors, inner }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, opacity: disabled ? 0.4 : 1 }}>
            <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                textTransform: 'uppercase', color: colors.labelMuted,
            }}>{label}</span>

            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 5 }}>
                {denoms.map(nominal => {
                    const count = Number(denominace[nominal] || 0);
                    return (
                        <DenomButton
                            key={nominal}
                            nominal={nominal}
                            count={count}
                            size={size}
                            disabled={disabled}
                            inner={inner}
                            colors={colors}
                            onPointerDown={() => !disabled && onPointerDown(nominal)}
                            onPointerUp={() => !disabled && onPointerUp(nominal)}
                            onPointerCancel={onPointerCancel}
                        />
                    );
                })}
            </div>
        </div>
    );
}

// ─── Tlačítko nominálu ────────────────────────────────────────────────────────

function DenomButton({ nominal, count, size, disabled = false, inner, colors, onPointerDown, onPointerUp, onPointerCancel }) {
    const aktivni = count > 0;
    const btnHeight = size === 'large' ? 54 : 50;
    const valFont = size === 'large' ? 18 : 16;

    return (
        <button type="button"
            disabled={disabled}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerCancel}
            onPointerCancel={onPointerCancel}
            style={{
                height: btnHeight,
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 2,
                padding: '4px 6px',
                background: aktivni ? `${ACCENT}1A` : inner.background,
                border: `1px solid ${aktivni ? ACCENT : inner.border.split(' ').pop()}`,
                borderRadius: SHEET_TOKENS.radius.input,
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: aktivni ? ACCENT : colors.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitTapHighlightColor: 'transparent',
                transition: 'background 0.12s, border-color 0.12s, color 0.12s',
                boxShadow: aktivni ? `0 0 0 1px ${ACCENT}26` : 'none',
            }}>
            <span style={{ fontSize: valFont, fontWeight: 800, lineHeight: 1 }}>{nominal}</span>
            <span style={{
                fontSize: 10, fontWeight: 700,
                color: aktivni ? ACCENT : colors.labelMuted,
                lineHeight: 1, minHeight: 11,
            }}>{count > 0 ? `${count}×` : '0×'}</span>
        </button>
    );
}

// ─── Helper pro pill tlačítka (Zpět / Vynulovat / Ručně) ──────────────────────
//
// 3. parametr `active=true` rozsvítí pill accentem (#FF8A00) — používá Ručně/Klikat
// toggle aby uživatel viděl, ve kterém režimu právě je.

function pillButton(colors, enabled, active = false) {
    return {
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '5px 10px', borderRadius: SHEET_TOKENS.radius.chip,
        background: active ? `${ACCENT}1A` : 'transparent',
        border: `1px solid ${active ? ACCENT : colors.divider}`,
        color: active ? ACCENT : (enabled ? colors.textSecondary : colors.textMuted),
        fontSize: 11.5, fontWeight: active ? 700 : 600,
        cursor: enabled ? 'pointer' : 'not-allowed',
        opacity: enabled ? 1 : 0.45,
    };
}
